import { Component, ViewEncapsulation } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@environment';
import { LoginService, WebClient } from '@injectables';
import { firstValueFrom, op } from '@lib/rxjs';
import { Client, User } from '@models';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { UploadProfileImageDialogComponent } from './upload-profile-image-dialog.component';
import { getUserAvatarUrl } from '../avatar-url';
import { getClientLogoUrl } from '@app/utilities/client-logo-url';

@Component({
    selector: 'profile-dropdown',
    templateUrl: './profile-dropdown.component.html',
    styleUrls: ['./profile-dropdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ProfileDropdownComponent {
    public saving = false;
    public logoSaving = false;
    public avatarLoaded = false;
    public avatarLoadFailed = false;
    public logoLoaded = false;
    public logoLoadFailed = false;
    private avatarCacheKey = Date.now();
    private logoCacheKey = Date.now();

    public constructor(
        public readonly user: User,
        private readonly webClient: WebClient,
        private readonly loginService: LoginService,
        private readonly httpClient: HttpClient,
        private readonly matDialog: MatDialog,
        private readonly dialog: DialogService,
        private readonly snackBar: MatSnackBar,
    ) { }

    public readonly clientName$ = this.webClient.model.client.search$({ where: { id: this.user.clientId } })
        .pipe(op.map((clients: Client[]) => clients[0]?.name || ''));

    public get initial() {
        return (this.user.displayName() || this.user.email || '?').trim().charAt(0).toUpperCase();
    }

    public get profileTitle() {
        return this.user.displayName() || this.user.email || 'User';
    }

    public get profileSubtitle() {
        return this.user.displayName() ? this.user.email : '';
    }

    public get previewAvatarUrl() {
        return getUserAvatarUrl(this.user.id, this.avatarCacheKey);
    }

    public get hasAvatar() {
        return this.avatarLoaded && !this.avatarLoadFailed;
    }

    public get companyLogoUrl() {
        return getClientLogoUrl(this.user.clientId, this.logoCacheKey);
    }

    public get hasCompanyLogo() {
        return this.logoLoaded && !this.logoLoadFailed;
    }

    public get canManageCompanyLogo() {
        return !!this.user.clientId && this.user.permissions >= 2;
    }

    public get isBusy() {
        return this.saving || this.logoSaving;
    }

    public async openUploadDialog() {
        const file = await firstValueFrom(this.matDialog.open(UploadProfileImageDialogComponent, {
            ...UploadProfileImageDialogComponent.config,
            data: {
                title: 'Upload Profile Image',
                uploadText: this.hasAvatar ? 'Replace' : 'Upload',
            },
        }).afterClosed());

        if (!file) {
            return;
        }

        this.saving = true;
        try {
            await this.uploadAvatar(file);
            this.refreshAvatar();
            this.snackBar.open('Profile updated successfully', 'OK', {
                duration: 4000,
                panelClass: ['snackbar-success']
            });
        } catch (error) {
            console.error('Error saving profile:', error);
            this.snackBar.open(this.getErrorMessage(error, 'Failed to save profile'), 'OK', {
                duration: 4000,
            });
        } finally {
            this.saving = false;
        }
    }

    public async openLogoUploadDialog() {
        const file = await firstValueFrom(this.matDialog.open(UploadProfileImageDialogComponent, {
            ...UploadProfileImageDialogComponent.config,
            data: {
                title: 'Upload Company Logo',
                labelText: 'Drag and drop a company logo here or click to browse',
                uploadText: this.hasCompanyLogo ? 'Replace' : 'Upload',
            },
        }).afterClosed());

        if (!file) {
            return;
        }

        this.logoSaving = true;
        try {
            await this.uploadCompanyLogo(file);
            this.refreshLogo();
            this.snackBar.open('Company logo updated successfully', 'OK', {
                duration: 4000,
                panelClass: ['snackbar-success']
            });
        } catch (error) {
            console.error('Error saving company logo:', error);
            this.snackBar.open(this.getErrorMessage(error, 'Failed to save company logo'), 'OK', {
                duration: 4000,
            });
        } finally {
            this.logoSaving = false;
        }
    }

    public async removeAvatar() {
        if (!this.hasAvatar || this.saving) {
            return;
        }

        const confirmed = await this.dialog.confirm({
            message: 'Remove the current profile image?',
            confirm: 'Remove',
        });

        if (!confirmed) {
            return;
        }

        this.saving = true;
        try {
            const response = await this.webClient.rest({
                route: 'user/avatar',
                method: 'delete',
                body: this.user.authentication(),
            });

            if (response.errors?.length) {
                throw new Error(response.errors?.[0] || 'Failed to remove profile image');
            }

            this.refreshAvatar();
            this.snackBar.open('Profile image removed successfully', 'OK', {
                duration: 4000,
                panelClass: ['snackbar-success']
            });
        } catch (error) {
            console.error('Error removing profile image:', error);
            this.snackBar.open(this.getErrorMessage(error, 'Failed to remove profile image'), 'OK', {
                duration: 4000,
            });
        } finally {
            this.saving = false;
        }
    }

    public async removeCompanyLogo() {
        if (!this.hasCompanyLogo || this.logoSaving) {
            return;
        }

        const confirmed = await this.dialog.confirm({
            message: 'Remove the current company logo?',
            confirm: 'Remove',
        });

        if (!confirmed) {
            return;
        }

        this.logoSaving = true;
        try {
            const response = await this.webClient.rest({
                route: 'client/logo',
                method: 'delete',
                body: {
                    ...this.user.authentication(),
                    clientId: this.user.clientId,
                },
            });

            if (response.errors?.length) {
                throw new Error(response.errors?.[0] || 'Failed to remove company logo');
            }

            this.refreshLogo();
            this.snackBar.open('Company logo removed successfully', 'OK', {
                duration: 4000,
                panelClass: ['snackbar-success']
            });
        } catch (error) {
            console.error('Error removing company logo:', error);
            this.snackBar.open(this.getErrorMessage(error, 'Failed to remove company logo'), 'OK', {
                duration: 4000,
            });
        } finally {
            this.logoSaving = false;
        }
    }

    public onAvatarLoaded() {
        this.avatarLoaded = true;
        this.avatarLoadFailed = false;
    }

    public onAvatarError() {
        this.avatarLoaded = false;
        this.avatarLoadFailed = true;
    }

    public onLogoLoaded() {
        this.logoLoaded = true;
        this.logoLoadFailed = false;
    }

    public onLogoError() {
        this.logoLoaded = false;
        this.logoLoadFailed = true;
    }

    private async uploadAvatar(file: File): Promise<void> {
        const formData = new FormData();
        const auth = this.user.authentication();
        formData.append('email', auth.email);
        formData.append('authToken', auth.authToken);
        formData.append('file', file, file.name);

        await firstValueFrom(this.httpClient.post<{ imageUrl: string }>(
            `${environment.backendUrl}/user/avatar`,
            formData
        ));
    }

    private async uploadCompanyLogo(file: File): Promise<void> {
        const formData = new FormData();
        const auth = this.user.authentication();
        formData.append('email', auth.email);
        formData.append('authToken', auth.authToken);
        formData.append('clientId', `${this.user.clientId}`);
        formData.append('file', file, file.name);

        await firstValueFrom(this.httpClient.post<{ imageUrl: string }>(
            `${environment.backendUrl}/client/logo`,
            formData
        ));
    }

    private getErrorMessage(error: unknown, fallback: string) {
        if (error instanceof HttpErrorResponse) {
            const apiError = error.error?.errors;
            if (Array.isArray(apiError) && apiError.length > 0) {
                return apiError[0];
            }
        }

        if (error instanceof Error && error.message) {
            return error.message;
        }

        return fallback;
    }

    private refreshAvatar() {
        this.avatarLoaded = false;
        this.avatarLoadFailed = false;
        this.avatarCacheKey = Date.now();
        const nextUser = Object.assign(new User(), this.user);
        this.loginService.user$.next(nextUser);
    }

    private refreshLogo() {
        this.logoLoaded = false;
        this.logoLoadFailed = false;
        this.logoCacheKey = Date.now();
        const nextUser = Object.assign(new User(), this.user);
        this.loginService.user$.next(nextUser);
    }
}
